import { useSnackbar } from 'notistack'
import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  createUser as createUserService,
  updateUser as updateUserService,
  deleteUser as deleteUserService,
  archiveUser as archiveUserService,
  getUsersSkipLimit as getUsersService,
  resetPassword as resetPasswordService,
  invite as inviteService,
  bulkInvite as bulkInviteService,
  bulkResetPassword as bulkResetPasswordService,
  getUsersByRoleIdSkipLimit as getUsersByRoleIdSkipLimitSerivce,
  updateBatchRoles as updateBatchRolesService
} from '../../services/user/user.service.js'
import { signInImpersonate } from '../../services/auth/auth.service.js'
import { validate } from '../../utils/index.js'
import { handlerErrorV2 } from '../../utils/errors.jsx'
import { updateUsersList, updateUsersListMethod } from '../../store/actions/user.actions.js'
import isEmpty from 'lodash/isEmpty'
import { toggleAlertModule } from '../../store/actions/alert.actions'
import SessionHelper from '../../helper/session.helper.js'
import { permissionsMenu } from '../../store/actions/menuActions.js'
import { changedCurriculum } from '../../store/actions/curriculum.actions.js'

const useUsers = () => {
  const licenses = useSelector((state) => state?.tenant?.license)
  const featureFlag = useSelector((state) => state?.tenant?.featureFlag || {})

  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const [isLoadingList, setIsLoadingList] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  const getUsers = useCallback(async (skip, rowsPerPage, filter) => {
    setIsLoadingList(true)
    try {
      const filterData = {
        ...filter
      }
      delete filterData.roles
      delete filterData.yearLevel
      delete filterData.flagId

      if (filterData?.flag?.length > 0) {
        filterData.flag = filterData.flag?.map((item) => item?.name)
      } else {
        delete filterData.flag
      }

      const response = await getUsersService(skip, rowsPerPage, filterData)
      if (!response?.data && response?.error) {
        enqueueSnackbar(response.error.message || 'An error ocurred', { variant: 'error' })
      } else {
        dispatch(updateUsersList(response?.data || { users: [], count: 0 }))
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      enqueueSnackbar(message, { variant: 'error' })
    } finally {
      setIsLoadingList(false)
    }
  }, [])

  const getUsersByRoleId = useCallback(async (roleId, skip, rowsPerPage, filter) => {
    setIsLoadingList(true)
    try {
      const filterData = {
        ...filter
      }
      delete filterData.roles
      delete filterData.yearLevel
      const response = await getUsersByRoleIdSkipLimitSerivce(roleId, skip, rowsPerPage, filterData)
      if (!response?.data && response?.error) {
        enqueueSnackbar(response.error.message || 'An error ocurred', { variant: 'error' })
      } else {
        dispatch(updateUsersList(response?.data || { users: [], count: 0 }))
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      enqueueSnackbar(message, { variant: 'error' })
    } finally {
      setIsLoadingList(false)
    }
  }, [])

  const validateForm = (form) => {
    const fieldRules = {
      graduationYear: 'required|number|numberPositive|length:4',
      yearLevelId: 'required',
      code: 'required|withoutSpaces',
      email: 'required|email',
      firstName: 'required',
      roles: 'required',
      lastName: 'required'
    }

    if (form.roles.some((role) => 'Student' === role.name)) {
      fieldRules.graduationYear = 'required|number|numberPositive|length:4'
      fieldRules.yearLevelId = 'required'
    } else if (form.roles.some((role) => 'Parent' === role.name)) {
      fieldRules.lastName = ''
      fieldRules.graduationYear = ''
      fieldRules.yearLevelId = ''
    } else {
      fieldRules.graduationYear = ''
      fieldRules.yearLevelId = ''
      fieldRules.lastName = 'required'
    }

    const errors = validate(fieldRules, form)
    if (!isEmpty(errors)) {
      return { error: true, ...errors }
    }

    return { error: false }
  }

  const formData = (form) => {
    const formData = {
      address: form.address,
      birthdate: form.birthdate,
      city: form.city,
      code: form.code,
      country: form.country,
      email: form.email,
      firstName: form.firstName,
      gender: form.gender,
      lastName: form.lastName,
      photo: form.photo,
      postalCode: form.postalCode,
      roles: form.rolesId,
      state: form.state,
      telephone: form.telephone,
      title: form.title,
      preferredName: form.preferredName
    }

    if (form.roles.some((role) => 'Student' === role.name)) {
      formData.data = {
        form: form.form,
        graduationYear: form.graduationYear,
        house: form.house,
        yearLevel: Array.isArray(form.yearLevelId) ? form.yearLevelId[0] : form.yearLevelId,
        flag: form?.data?.flag || []
      }
    }

    return formData
  }

  const createUser = async (form = {}) => {
    const result = validateForm(form)
    if (result.error) {
      return result
    }

    try {
      const data = formData(form)
      const response = await createUserService(data)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        dispatch(updateUsersListMethod('POST', response.data))
        enqueueSnackbar('User created successfully', { variant: 'success' })
        dispatch(toggleAlertModule())

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const updateUser = async (userId, form = {}) => {
    const result = validateForm(form)
    if (result.error) {
      return result
    }

    try {
      const data = formData(form)
      const response = await updateUserService(userId, data)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        dispatch(updateUsersListMethod('PUT', response.data))
        enqueueSnackbar('User updated successfully', { variant: 'success' })
        dispatch(toggleAlertModule())

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const deleteUser = async (userId) => {
    try {
      const response = await deleteUserService(userId)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        dispatch(updateUsersListMethod('DELETE', userId))
        enqueueSnackbar('User deleted successfully', { variant: 'success' })

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const archiveUser = async (userId) => {
    try {
      const response = await archiveUserService(userId)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        dispatch(updateUsersListMethod('DELETE', userId))
        enqueueSnackbar('User archived successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const resetPassword = async (userId, email) => {
    enqueueSnackbar(`Sending email to ${email}`, { variant: 'info' })
    setIsLoading(true)
    try {
      const response = await resetPasswordService(userId)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        enqueueSnackbar(response.data.message, { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      setIsLoading(false)
    }
  }

  const invite = async (userId, email) => {
    enqueueSnackbar(`Sending email to ${email}`, { variant: 'info' })
    setIsLoading(true)
    try {
      const response = await inviteService(userId)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        const data = {
          lastInvited: new Date(),
          invited: 1,
          _id: userId
        }
        dispatch(updateUsersListMethod('PUT', data))
        enqueueSnackbar(response.data.message, { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      setIsLoading(false)
    }
  }

  const bulkInvite = async (userIds) => {
    enqueueSnackbar(`Sending email to users selected`, { variant: 'info' })
    setIsLoading(true)
    try {
      const response = await bulkInviteService({ userIds })
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      setIsLoading(false)
    }
  }
  const bulkResetPassword = async (userIds) => {
    enqueueSnackbar(`Sending email to users selected`, { variant: 'info' })
    setIsLoading(true)
    try {
      const response = await bulkResetPasswordService({ userIds })
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      setIsLoading(false)
    }
  }

  const loginImpersonate = async (userId) => {
    try {
      const response = await signInImpersonate(userId)
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        SessionHelper.saveSession(response.data)
        const charts = await dispatch(permissionsMenu(response.data, null, licenses, featureFlag)).catch(
          () => []
        )
        dispatch(changedCurriculum())
        return { error: false, charts, userRoles: response.data.roles || [] }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }
  const updateBatchRoles = async (userIds, form, mode = 'add') => {
    try {
      setIsLoading(true)
      const response = await updateBatchRolesService({ userIds, rolesId: form.rolesId, mode })
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred' }
      } else {
        dispatch(updateUsersListMethod('BATCH_PUT', response.data))
        enqueueSnackbar('Users updated successfully', { variant: 'success' })

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoadingList,
    isLoading,
    getUsers,
    getUsersByRoleId,
    createUser,
    updateUser,
    deleteUser,
    archiveUser,
    invite,
    bulkInvite,
    bulkResetPassword,
    resetPassword,
    loginImpersonate,
    updateBatchRoles
  }
}

export default useUsers
